/**
 * Preview image registry for Scene 4 "Your Path" day-beat blocks.
 *
 * Source of truth for artifact screenshots referenced in the landing
 * cinematic scrolltell. Dimensions captured at viewport 1440×900 with
 * deviceScaleFactor=2 (native 2880×1800) — Next Image reserves the aspect
 * ratio from `width × height` to avoid CLS while webp decodes.
 *
 * Each registry value is either a single PreviewImage or a tuple of frames
 * forming a story arc; carousel rendering kicks in when `length >= 2`. All
 * captures come from real prod pages whose backing rows are locked via
 * migration 079. Re-capture: `cd foundry-app && node tmp/preview-carousel.mjs`
 * then `cwebp -q 82 -m 6 <png> -o <webp>`.
 */

export interface PreviewImage {
  /** Public path (prefixed with `/`, served from `/public`). */
  src: string;
  /** Intrinsic width in pixels (captured at deviceScaleFactor=2). */
  width: number;
  /** Intrinsic height in pixels. */
  height: number;
  /** Descriptive alt text — screen reader reads, SEO indexes. */
  alt: string;
}

/** Tuple of at-least-one frames; PreviewCarousel auto-advances when ≥2. */
export type PreviewSequence = readonly [PreviewImage, ...PreviewImage[]];

const COMMON: Pick<PreviewImage, "width" | "height"> = { width: 1440, height: 900 };

/**
 * Plan tour — Sponsor Pay Desk (A+ grade, $149K/yr). Source rows locked
 * via migration 080: business_plans.id OvDE2v5S_WdnhzbIwG3_3, session
 * _ZXpzzWQuca811nKm0Sle, niche KJLScElxqbF3xnTeWYHH6.
 *
 * Story arc: dream → market opportunity → pricing model → break-even moment
 * → capital needed → execution rings.
 */
const PLAN_TOUR: PreviewSequence = [
  {
    ...COMMON,
    src: "/landing-v2/previews/plan-1-hero.webp",
    alt:
      "Plan v2 hero card — Sponsor Pay Desk with foil A+ grade and $149K per year revenue projection",
  },
  {
    ...COMMON,
    src: "/landing-v2/previews/plan-2-market.webp",
    alt:
      "Plan v2 market waterfall — TAM $6.36B, SAM $148M, SOM $84.96M ARR with growing 19 percent CAGR and three Why Now drivers",
  },
  {
    ...COMMON,
    src: "/landing-v2/previews/plan-3-pricing.webp",
    alt:
      "Plan v2 pricing tiers — Pay Desk $59 per month and Manager Desk $99 per month with feature lists, why-this-price reasoning, and 6-week MVP scope",
  },
  {
    ...COMMON,
    src: "/landing-v2/previews/plan-4-breakeven.webp",
    alt:
      "Plan v2 break-even moment — 136 customers by Month 10, $59 per month times 136 equals $8,024 covering $8,000 per month burn, plus unit economics panel",
  },
  {
    ...COMMON,
    src: "/landing-v2/previews/plan-5-capital.webp",
    alt:
      "Plan v2 capital needed — $8,000 per month ring chart broken down by spend category fits inside founder's $10K budget with $2K headroom",
  },
  {
    ...COMMON,
    src: "/landing-v2/previews/plan-6-rings.webp",
    alt:
      "Plan v2 phase rings — 90 days, 3 phases, first dollar by Week 4, Apple Watch-style timeline with three phase cards",
  },
];

/**
 * Brand tour — primarily DueProof (Sponsor Pay Desk brand_kits.id
 * yzkPCCL5clC-5gJSbZ3Du), with the Where-I-Stand frame pulled from
 * LaunchLint (Steam Wishlist Clinic brand_kits.id xqqbaePgPvf5y4W14EEuw)
 * because its scatter visualization reads more striking than DueProof's
 * own quadrant. Both source rows + their parent niches and sessions are
 * locked via migration 080.
 *
 * Story arc: identity → naming options → palette pick → voice pick → positioning.
 */
const BRAND_TOUR: PreviewSequence = [
  {
    ...COMMON,
    src: "/landing-v2/previews/brand-1-cover.webp",
    alt:
      "Brand v2 hero cover — DueProof identity card with DU logomark, name, tagline, and palette swatches",
  },
  {
    ...COMMON,
    src: "/landing-v2/previews/brand-2-names.webp",
    alt:
      "Brand v2 naming options — Five names One of them is mine, with DueProof picked as Top Pick at 8.0 plus four alternates ProofDesk, TermsFirst, SlatePay, Receiva",
  },
  {
    ...COMMON,
    src: "/landing-v2/previews/brand-3-palettes.webp",
    alt:
      "Brand v2 palettes — Three palettes survived Pick yours, Premium tab selected showing Archive Ink #0F2437 dark navy hero",
  },
  {
    ...COMMON,
    src: "/landing-v2/previews/brand-4-voices.webp",
    alt:
      "Brand v2 voices — Three voices One is mine for the next decade, Modern and Clean tab selected with DueProof landing preview rendered in that voice",
  },
  {
    ...COMMON,
    src: "/landing-v2/previews/brand-5-positioning.webp",
    alt:
      "Brand v2 positioning map — Here's where no one else stands, scatter chart with You claiming the open quadrant against Steam Page Analyzer, Wishlist Engine, Upwork Steam, and other gaming-tooling competitors",
  },
];

export const PREVIEW_IMAGES = {
  planTour: PLAN_TOUR,
  brandTour: BRAND_TOUR,
} as const satisfies Record<string, PreviewSequence>;
